import React, { useState, useEffect } from 'react';
import { authService } from '../services/api';

function ApproveOrders() {
  const [weeklyOrders, setWeeklyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adjustedQuantity, setAdjustedQuantity] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const user = authService.getCurrentUser();

  useEffect(() => {
    loadPendingOrders();
  }, []);

  const loadPendingOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://b1.envicblue.co.uk/api/weekly_orders.php?action=pending', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setWeeklyOrders(Array.isArray(data.data) ? data.data : []);
      } else {
        setWeeklyOrders([]);
        setError(data.message || 'Failed to load orders');
      }
    } catch (err) {
      setWeeklyOrders([]);
      setError('Failed to load pending orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openApprovalModal = (order) => {
    setSelectedOrder(order);
    setAdjustedQuantity(order.total_quantity);
    setApprovalNotes('');
    setShowModal(true);
  };

  const handleApprove = async () => {
    if (!selectedOrder || !adjustedQuantity || adjustedQuantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    try {
      const response = await fetch(`https://b1.envicblue.co.uk/api/weekly_orders.php?action=approve&week_id=${selectedOrder.weekly_order_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adjusted_quantity: adjustedQuantity,
          notes: approvalNotes
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Order approved successfully!');
        setShowModal(false);
        setSelectedOrder(null);
        loadPendingOrders();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (err) {
      alert('❌ Failed to approve: ' + err.message);
    }
  };

  const handleReject = async (order) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return;

    try {
      const response = await fetch(`https://b1.envicblue.co.uk/api/weekly_orders.php?action=reject&week_id=${order.weekly_order_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: reason })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Order rejected');
        loadPendingOrders();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (err) {
      alert('❌ Failed to reject: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>⏳</div>
          <div style={{ color: '#666', marginTop: '10px' }}>Loading orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
          ✅ Approve Weekly Orders
        </h1>
        <p style={{ color: '#666' }}>Review and approve orders from staff members</p>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          color: '#c33',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '16px',
        marginBottom: '30px'
      }}>
        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
          📅 Pending Approvals
        </div>
        <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
          {weeklyOrders.length}
        </div>
      </div>

      {weeklyOrders.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
          <h3 style={{ fontSize: '20px', color: '#333', marginBottom: '10px' }}>All Caught Up!</h3>
          <p style={{ color: '#666' }}>No pending orders to review</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {weeklyOrders.map((order) => {
            const isLowStock = order.total_quantity < order.min_stock_level;
            const isHighStock = order.total_quantity > order.max_stock_level;

            return (
              <div key={order.weekly_order_id} style={{
                background: 'white',
                borderRadius: '16px',
                padding: '25px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                border: isHighStock ? '2px solid #ef4444' : isLowStock ? '2px solid #f59e0b' : 'none'
              }}>
                {/* Alert Badge */}
                {(isHighStock || isLowStock) && (
                  <div style={{
                    background: isHighStock ? '#ef444420' : '#f59e0b20',
                    color: isHighStock ? '#ef4444' : '#f59e0b',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    ⚠️ {isHighStock ? 'ABOVE MAXIMUM STOCK LEVEL' : 'BELOW MINIMUM STOCK LEVEL'}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', alignItems: 'start' }}>
                  {/* Left side - Product info */}
                  <div>
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>
                        {order.product_code}
                      </div>
                      <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                        {order.product_name}
                      </h3>
                      <p style={{ fontSize: '14px', color: '#666' }}>
                        {order.product_subtitle}
                      </p>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '15px',
                      marginBottom: '15px'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '3px' }}>Requested Qty</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                          {order.total_quantity}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '3px' }}>Current Stock</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
                          {order.current_stock}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '3px' }}>Stock Range</div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          {order.min_stock_level} - {order.max_stock_level}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '3px' }}>No. of Orders</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#8b5cf6' }}>
                          {order.order_count}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '150px' }}>
                    <button
                      onClick={() => openApprovalModal(order)}
                      style={{
                        padding: '12px 20px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => handleReject(order)}
                      style={{
                        padding: '12px 20px',
                        background: '#fff',
                        color: '#ef4444',
                        border: '2px solid #ef4444',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approval Modal */}
      {showModal && selectedOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setShowModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%'
          }}
          onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
              ✅ Approve Order
            </h2>

            <div style={{
              background: '#f9f9f9',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Product</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                {selectedOrder.product_name}
              </div>
              <div style={{ fontSize: '13px', color: '#999', marginTop: '5px' }}>
                Code: {selectedOrder.product_code}
              </div>
              <div style={{ 
                marginTop: '10px', 
                paddingTop: '10px', 
                borderTop: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px'
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#999' }}>Requested</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#667eea' }}>
                    {selectedOrder.total_quantity}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#999' }}>Current Stock</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                    {selectedOrder.current_stock}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Approve Quantity *
              </label>
              <input
                type="number"
                min="1"
                value={adjustedQuantity}
                onChange={(e) => setAdjustedQuantity(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  boxSizing: 'border-box'
                }}
              />
              {adjustedQuantity != selectedOrder.total_quantity && (
                <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '5px' }}>
                  ⚠️ You're adjusting from {selectedOrder.total_quantity} to {adjustedQuantity}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Notes (Optional)
              </label>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                rows="3"
                placeholder="Add approval notes..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✅ Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApproveOrders;
